import React, {
	useMemo, useEffect, useCallback, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Socket from '../../../connection/socket';
import { LoadMeeting } from '../../../connection/meeting';
import PresenterSelectMeeting from '../presenter-select-meeting';
import ProducerSelectMeeting from '../producer/producer-select-meeting';
import Templates from './templates';
import Team from './team';
import HomeSettings from './home-settings';
import HomeHeader from './home-header';
import { HOME_ROUTES, HOME_ROUTE_ICONS, Channel } from '../../../stores/reducers/home';
import * as ContentActions from '../../../stores/actions/content';
import * as HomeActions from '../../../stores/actions/home';
import * as SessionActions from '../../../stores/actions/session';
import * as InvitationActions from '../../../stores/actions/invitations';
import LoadingSpinner from '../../general-components/loading-spinner';
import Colors from '../../../utils/colors';
import { getImageLocation, whichChannelLogoToUse } from '../../../utils/utils';
import ShowDetails from './show-details/show-details-main';
import TellyChannelLogo from '../../../images/app-store-icon.png';
import Icon, { COLORS } from '../../general-components/icon';
import useMediaDevices from '../../../hooks/use-media-devices';
import { APP } from '../../../stores/session-status';
import { ShowSession, ShowSessionType } from '../../../stores/reducers/session';
import useHome from '../../../hooks/use-home';
import { GreenroomState } from '../../../stores/types/types';
import { showAlert } from '../../general-components/alert-service';
import MeetingModal from '../producer/meeting-modal';
import { setStorageItem, getStorageItem } from '../../../utils/local-storage';
import ImageFrame from '../../general-components/image-frame';

interface Props {
	socket: Socket | null;
	connected: boolean;
}

export default function HomeContainer(props: Props) {
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const eventsChannels = useSelector(
		(state: GreenroomState) => state.SessionReducer.eventsChannels,
	);
	const eventsLegacyChannels = useSelector(
		(state: GreenroomState) => state.SessionReducer.eventsLegacyChannels,
	);
	const grChannels = useSelector((state: GreenroomState) => state.SessionReducer.grChannels);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const showSessionTypes = useSelector(
		(state: GreenroomState) => state.SessionReducer.showSessionTypes,
	);
	const showroomsTokens = useSelector(
		(state: GreenroomState) => state.SessionReducer.showroomsTokens,
	);
	const appSignedInWith = useSelector(
		(state: GreenroomState) => state.SessionReducer.appSignedInWith,
	);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const justVerified = useSelector((state: GreenroomState) => state.SessionReducer.justVerified);
	const meetingModalOpen = useSelector(
		(state: GreenroomState) => state.SessionReducer.meetingModalOpen,
	);
	const meetingToEdit = useSelector((state: GreenroomState) => state.SessionReducer.meetingToEdit);
	const currentRoute = useSelector((state: GreenroomState) => state.HomeReducer.currentRoute);
	const selectedChannel = useSelector((state: GreenroomState) => state.HomeReducer.selectedChannel);
	const events = useSelector((state: GreenroomState) => state.ContentReducer.events);
	const loadingEvents = useSelector((state: GreenroomState) => state.ContentReducer.loadingEvents);
	const teamsLoading = useSelector((state: GreenroomState) => state.HomeReducer.teamsLoading);
	const homeMeeting = useSelector((state: GreenroomState) => state.HomeReducer.homeMeeting);

	const { socket, connected } = props;
	const dispatch = useDispatch();
	const { checkMediaDevices } = useMediaDevices();
	const { filteredMeetingsByChannel } = useHome();

	const [eventsAndShowSessions, setEventsAndShowSessions] = useState<any[] | undefined>();

	const previousHomeChannel = (app: string): number | undefined => {
		const chan = getStorageItem(`home-channel=${app}`);
		return chan ? parseInt(chan, 10) : undefined;
	};

	const loadMeetings = () => {
		if (host) {
			switch (appSignedInWith) {
				case APP.EVENTS:
					if (token && selectedChannel) {
						dispatch(ContentActions.loadMeetingsEventsProducer(token, selectedChannel?.channel));
					}
					break;
				case APP.EVENTS_LEGACY:
					if (token) {
						dispatch(ContentActions.loadMeetingsEventsLegacyProducer(token));
					}
					break;
				case APP.SHOWROOMS:
					if (showroomsTokens) {
						dispatch(ContentActions.loadMeetingsShowroomsProducer(showroomsTokens.accessToken));
					}
					break;
				case APP.GREENROOM:
					if (token) {
						dispatch(ContentActions.loadMeetingsStandaloneGreenroomProducer(token));
					}
					break;
				default:
					break;
			}
		} else if (token) {
			dispatch(ContentActions.loadMeetingsPresenter(token));
		}
	};

	useEffect(() => {
		if (token && !homeMeeting) {
			if (appSignedInWith !== APP.EVENTS) {
				// Events is the only app where we load GRs when
				// the selected channel changes (see useEffect below)
				loadMeetings();
			}
			dispatch(HomeActions.loadNotifications(token));
			dispatch(SessionActions.getShowSessionTypes(token));
		}
	}, [dispatch, token, homeMeeting]);

	useEffect(() => {
		if (justVerified && (!host || user?.gr_producer)) {
			showAlert({
				message: 'Verification Successful',
				duration: 5000,
			});

			dispatch(SessionActions.setJustVerified(false));
		}
	}, [dispatch, host, justVerified, user]);

	useEffect(() => {
		if (appSignedInWith === APP.EVENTS && !homeMeeting) {
			loadMeetings();
		}
	}, [selectedChannel, token, homeMeeting]);

	useEffect(() => {
		if (host) { checkMediaDevices(); }
	}, [host]);

	useEffect(() => {
		if (token) {
			dispatch(HomeActions.loadNotifications(token));
		}
	}, [dispatch, token, events]);

	useEffect(() => {
		const channels = {
			events_channels: eventsChannels,
			events_legacy_channels: eventsLegacyChannels,
			gr_channels: grChannels,
		};

		if (token && !teamsLoading) {
			dispatch(HomeActions.loadTeams(token, channels));
		}

		// update the selected channel for standalone greenroom if the settings have changed
		const currentSelectedGrChannel = grChannels.find(
			(channel: Channel) => channel.channel === selectedChannel?.channel,
		);
		if (currentSelectedGrChannel) {
			dispatch(HomeActions.setHomeChannel(currentSelectedGrChannel));
		}
	}, [eventsChannels, eventsLegacyChannels, grChannels, token]);

	useEffect(() => {
		if (!selectedChannel && host) {
			const prevChannelId = previousHomeChannel(appSignedInWith);
			if (prevChannelId) {
				let channelsToUse = grChannels;
				if (appSignedInWith === APP.EVENTS) {
					channelsToUse = eventsChannels;
				}
				if (appSignedInWith === APP.EVENTS_LEGACY) {
					channelsToUse = eventsLegacyChannels;
				}
				const prevChannel = channelsToUse.find((c) => c.channel === prevChannelId);
				if (prevChannel) {
					dispatch(HomeActions.setHomeChannel(prevChannel));
					return;
				}
			}

			if (appSignedInWith === APP.EVENTS && eventsChannels && eventsChannels[0]) {
				dispatch(HomeActions.setHomeChannel(eventsChannels[0]));
				return;
			}

			if (appSignedInWith === APP.EVENTS_LEGACY
				&& eventsLegacyChannels
				&& eventsLegacyChannels[0]
			) {
				dispatch(HomeActions.setHomeChannel(eventsLegacyChannels[0]));
				return;
			}

			if (appSignedInWith === APP.GREENROOM && grChannels && grChannels[0]) {
				dispatch(HomeActions.setHomeChannel(grChannels[0]));
			}
		}
	}, [appSignedInWith, selectedChannel, host, eventsLegacyChannels, grChannels]);

	useEffect(() => {
		const showSessionsAsEvents: any[] = [];
		const eventsWithSessionData = filteredMeetingsByChannel().map((event: any) => {
			if (event.show_sessions) {
				event.show_sessions.forEach((session: ShowSession) => {
					const filteredSessionInvites = event.invites.filter(
						(invite: any) => session.invitations.some(
							(invitation: any) => invite.email === invitation.email,
						),
					);
					showSessionsAsEvents.push({
						...event,
						...session,
						invites: filteredSessionInvites,
						show_sessions: [],
						show_session_type_id: session.session_type_id,
						show_session_type: !showSessionTypes || showSessionTypes.length === 0 ? 'Broadcast'
							: showSessionTypes.find((showSessionType: ShowSessionType) => showSessionType.id === session.session_type_id)?.name ?? 'Broadcast',
					});
				});
			}

			return {
				...event,
				show_session_type_id: 0,
				show_session_type: 'Broadcast',
				show_sessions: [],
			};
		});

		/*
		Child components rely on `eventsAndShowSessions` (initially undefined) to
		eventually be an array (empty or filled). It is initially left undefined
		because we want to differentiate between events / show sessions still being
		loaded (undefined) and not having any events / show sessions (empty array).

		Relying on `ContentReducer` -> `loadingEvents` is not sufficient because
		while `eventsAndShowSessions` is based on `events`, additional processing to
		filter, etc. causes a lag time that means events loaded (`!loadingEvents`)
		is not the same as `eventsAndShowSessions` being loaded. This leads to
		content flashing. This is resolved by relying on `eventsAndShowSessions`
		not being undefined.

		The other concern is that `eventsAndShowSessions` might stay undefined and
		cause child components to be stuck in an infinite loading state. This is
		not a worry, however, because this useEffect dependency array includes
		`events` which is initialized to [] and is used as the basis for
		`filteredMeetingsByChannel()`, which will be used to set
		`eventsAndShowSessions` as soon as events have finished loading.
		*/

		if (!loadingEvents) {
			setEventsAndShowSessions([...eventsWithSessionData, ...showSessionsAsEvents]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [events, loadingEvents, selectedChannel, showSessionTypes]);

	const channels = useMemo(() => {
		if (appSignedInWith === APP.EVENTS && eventsChannels && eventsChannels[0]) {
			return eventsChannels;
		} if (appSignedInWith === APP.GREENROOM && grChannels && grChannels[0]) {
			return grChannels;
		}
		return [];
	}, [appSignedInWith, eventsChannels, eventsLegacyChannels, grChannels]);

	const setSelectedChannel = (channel: Channel, appType: string) => {
		setStorageItem(`home-channel=${appType}`, channel.channel.toString());
		dispatch(HomeActions.setHomeChannel(channel));
	};

	const hasAllData = useMemo(() => true, []);

	const getIcon = useCallback((route: string): JSX.Element => {
		const iconName = HOME_ROUTE_ICONS[route];

		if (iconName) {
			return (
				<Icon
					name={iconName}
					color={currentRoute === HOME_ROUTES[route] ? COLORS.WHITE : COLORS.DARK_GRAY_2}
					size={13}
					className="margin-right-small"
				/>
			);
		}

		return <></>;
	}, [currentRoute]);

	async function reloadMeetings(isNewMeeting: boolean, channelId: number) {
		if (!token) return;

		// If it is a new meeting, the user is creating a new SGR from the main home view
		if (isNewMeeting) {
			// Display alert with message based on new meeting created or saving existing meeting
			showAlert({
				message: `Greenroom ${isNewMeeting ? 'Created!' : 'Saved!'}`,
				description: `Greenroom successfully ${isNewMeeting ? 'created and added to the list.' : 'saved and updated in the list.'}`,
				type: 'neutral',
				duration: 4000,
			});

			// Reload the list anytime a meeting is created/edited
			// Only SGR producer can create/update meetings in meeting modal which fires this function
			dispatch(ContentActions.loadMeetingsStandaloneGreenroomProducer(token));

			// Move to the shows tab if it is not currently open
			if (currentRoute !== HOME_ROUTES.SHOWS) {
				dispatch(HomeActions.setHomeRoute(HOME_ROUTES.SHOWS));
			}

			// Move to the new meeting's channel if it is not the currently selected one
			if (channelId !== selectedChannel?.channel) {
				const newMeetingChannel = grChannels.find(
					(channel: Channel) => channel.channel === channelId,
				);
				if (newMeetingChannel) { setSelectedChannel(newMeetingChannel, appSignedInWith); }
			}
		} else {
			// The user has updated an SGR from the Show Details page
			showAlert({
				message: 'Greenroom Saved',
				description: 'Greenroom successfully updated.',
				type: 'neutral',
				duration: 4000,
			});

			// Reload the meeting to get the updated show data
			const updatedMeeting = await LoadMeeting(token!, homeMeeting.meeting);

			if (updatedMeeting.settings_json) {
				const { entryWithoutProducer } = JSON.parse(updatedMeeting.settings_json);
				updatedMeeting.entry_without_producer = entryWithoutProducer === undefined
					? true : entryWithoutProducer;
			} else {
				updatedMeeting.entry_without_producer = true;
			}

			dispatch(HomeActions.setHomeMeeting(updatedMeeting));

			// Reload the meeting's invitations just in case any were added in the meeting modal.
			dispatch(InvitationActions.loadInvitations(token, homeMeeting.meeting));
		}
	}

	return (
		!hasAllData
			? <div className="loading-container">
				<LoadingSpinner
					height={100}
					width={100}
					color={Colors.red}
				/>
			</div>
			: <>
				<div className="home-wrapper flex">
					{host && currentRoute !== HOME_ROUTES.DETAILS
						&& (
							<div className="top-left-corner-logo">
								<div className="telly-channel-bubble">
									<img src={TellyChannelLogo} alt='telly' />
									<div className="small-separator"></div>
								</div>
							</div>
						)}

					<div className="home-menu-content-header-wrapper flex virtuoso-scroller">
						<div className={classNames(
							'home-header',
							{ 'presenter-home-header': host ? currentRoute === HOME_ROUTES.DETAILS : !host },
						)}>
							<HomeHeader />
						</div>
						<div
							className={classNames(
								'home-body',
								{ producer: host ? currentRoute !== HOME_ROUTES.DETAILS : false },
							)}
							id="home-body-scroller"
						>
							{currentRoute !== HOME_ROUTES.DETAILS && host && (
								<div className="home-sidebar-wrapper flex">
									{channels && channels.map((channel: Channel, index: number) => (
										<div
											className={classNames('home-channel-bubble-container flex', { selected: channel?.channel === selectedChannel?.channel })}
											onClick={() => setSelectedChannel(channel, appSignedInWith)}
											key={index}
											data-name={channel.name}
											data-testid={`channel-select-button=${channel?.channel ?? 0}`}
										>
											{(channel.logo_color || channel.logo_white) && (
												<img
													className="home-channel-bubble"
													src={getImageLocation(
														whichChannelLogoToUse(channel?.logo_white, channel?.logo_color),
													)}
												/>
											)}
											{!channel.logo_white && !channel.logo_color && (
												<div className="telly-channel-bubble-container">
													<div className="telly-channel-bubble">
														<ImageFrame
															imageSrc={TellyChannelLogo}
															altText="Telly default channel logo"
														/>
													</div>
												</div>
											)}
										</div>
									))}
								</div>
							)}
							<div className="home-menu-content-wrapper flex">
							{currentRoute !== HOME_ROUTES.DETAILS && host
								&& <div className="home-menu flex">
									{
										Object.entries(HOME_ROUTES).map((entry) => {
											// Hiding templates and settings for Greenroom 2.0
											if (entry[1] === HOME_ROUTES.DETAILS
												|| entry[1] === HOME_ROUTES.TEMPLATES
												|| (entry[1] === HOME_ROUTES.SETTINGS && appSignedInWith !== APP.GREENROOM)
											) {
												return null;
											}
											return <button key={entry[1]} className={classNames({ selected: currentRoute === entry[1] }, 'background-dark text-transform-capitalize round')}
													onClick={() => dispatch(HomeActions.setHomeRoute(entry[1]))}>
													{getIcon(entry[0])}
													<span className="button-text">{entry[0].toString().toLowerCase()}</span>
												</button>;
										})
									}
								</div>
							}

							{currentRoute === HOME_ROUTES.SHOWS
								&& <div className={classNames('home-shows flex', { presenter: !host })}>
									<div className="home-event-view flex margin-large">
										{host
											? <ProducerSelectMeeting
												connected={connected}
												eventsAndShowSessions={eventsAndShowSessions}
											/>
											: <PresenterSelectMeeting
												connected={connected}
												eventsAndShowSessions={eventsAndShowSessions}
												totalOnboardingBubbles={3}
											/>
										}
									</div>
								</div>
							}

							{currentRoute === HOME_ROUTES.TEMPLATES
								&& <div className="home-templates flex">
									<Templates socket={socket} />
								</div>
							}

							{currentRoute === HOME_ROUTES.TEAM
								&& <div className="home-team flex">
									<Team socket={socket} />
								</div>
							}

							{currentRoute === HOME_ROUTES.SETTINGS
								&& <div className="home-settings flex">
									<HomeSettings />
								</div>
							}

							{currentRoute === HOME_ROUTES.DETAILS
								&& <div className="home-show-details flex">
									<ShowDetails connected={connected} socket={socket} />
								</div>
							}

							{meetingModalOpen
								&& <MeetingModal
									editMeeting={meetingToEdit}
									reloadMeetings={
										(isNewMeeting, channelId) => reloadMeetings(isNewMeeting, channelId)
									}
								/>
							}
						</div>
						</div>
					</div>
				</div>
			</>
	);
}
